/**
 * Hand-coded surface→lemma table for the most frequent Sanskrit
 * pronouns, particles, and irregular verb forms — the words a suffix
 * table can't reach because their stems and surface forms are
 * suppletive (different roots for different cases) or because they
 * are uninflected indeclinables.
 *
 * Dramatically improves coverage on the unresolved tail of the
 * Prasthāna-trayī corpus (तेषां, येषां, इदम्, स्म, उवाच, अहम्, ...).
 */

export interface PronounEntry {
  lemma: string;
  label: string;
  /** Optional gloss used when no dictionary entry exists for the lemma. */
  gloss?: string;
}

export const PRONOUN_TABLE: Record<string, PronounEntry> = {
  // तद् paradigm (m./n./f.)
  सः: { lemma: 'तद्', label: 'm. nom. sg.', gloss: 'he, that' },
  तम्: { lemma: 'तद्', label: 'm. acc. sg.', gloss: 'him' },
  तेन: { lemma: 'तद्', label: 'm./n. instr. sg.', gloss: 'by him/it' },
  तस्मै: { lemma: 'तद्', label: 'm./n. dat. sg.', gloss: 'to him/it' },
  तस्मात्: { lemma: 'तद्', label: 'm./n. abl. sg.', gloss: 'from him/it; therefore' },
  तस्य: { lemma: 'तद्', label: 'm./n. gen. sg.', gloss: 'his' },
  तस्मिन्: { lemma: 'तद्', label: 'm./n. loc. sg.', gloss: 'in him/it' },
  ते: { lemma: 'तद्', label: 'm. nom. pl.', gloss: 'they (m.)' },
  तौ: { lemma: 'तद्', label: 'm. nom./acc. dual', gloss: 'those two' },
  तान्: { lemma: 'तद्', label: 'm. acc. pl.', gloss: 'them (m.)' },
  तैः: { lemma: 'तद्', label: 'm./n. instr. pl.', gloss: 'by them' },
  तेषाम्: { lemma: 'तद्', label: 'm./n. gen. pl.', gloss: 'of them' },
  तेषु: { lemma: 'तद्', label: 'm./n. loc. pl.', gloss: 'in them' },
  तत्: { lemma: 'तद्', label: 'n. nom./acc. sg.', gloss: 'that, it' },
  तानि: { lemma: 'तद्', label: 'n. nom./acc. pl.', gloss: 'those (n.)' },
  सा: { lemma: 'तद्', label: 'f. nom. sg.', gloss: 'she, that' },
  ताम्: { lemma: 'तद्', label: 'f. acc. sg.', gloss: 'her' },
  तया: { lemma: 'तद्', label: 'f. instr. sg.', gloss: 'by her' },
  तस्यै: { lemma: 'तद्', label: 'f. dat. sg.', gloss: 'to her' },
  तस्याः: { lemma: 'तद्', label: 'f. abl./gen. sg.', gloss: 'her' },
  तस्याम्: { lemma: 'तद्', label: 'f. loc. sg.', gloss: 'in her' },
  ताः: { lemma: 'तद्', label: 'f. nom./acc. pl.', gloss: 'they/them (f.)' },

  // यद् paradigm
  यः: { lemma: 'यद्', label: 'm. nom. sg.', gloss: 'who, which' },
  यम्: { lemma: 'यद्', label: 'm. acc. sg.' },
  येन: { lemma: 'यद्', label: 'm./n. instr. sg.' },
  यस्मै: { lemma: 'यद्', label: 'm./n. dat. sg.' },
  यस्मात्: { lemma: 'यद्', label: 'm./n. abl. sg.', gloss: 'from which; because' },
  यस्य: { lemma: 'यद्', label: 'm./n. gen. sg.' },
  यस्मिन्: { lemma: 'यद्', label: 'm./n. loc. sg.' },
  ये: { lemma: 'यद्', label: 'm. nom. pl.' },
  यान्: { lemma: 'यद्', label: 'm. acc. pl.' },
  यैः: { lemma: 'यद्', label: 'm./n. instr. pl.' },
  येषाम्: { lemma: 'यद्', label: 'm./n. gen. pl.', gloss: 'of those who' },
  येषु: { lemma: 'यद्', label: 'm./n. loc. pl.' },
  यत्: { lemma: 'यद्', label: 'n. nom./acc. sg.', gloss: 'which, that' },
  यानि: { lemma: 'यद्', label: 'n. nom./acc. pl.' },
  या: { lemma: 'यद्', label: 'f. nom. sg.' },
  याम्: { lemma: 'यद्', label: 'f. acc. sg.' },
  ययोः: { lemma: 'यद्', label: 'm./n. gen./loc. dual' },

  // एतद्
  एषः: { lemma: 'एतद्', label: 'm. nom. sg.', gloss: 'this (m.)' },
  एष: { lemma: 'एतद्', label: 'm. nom. sg.', gloss: 'this (m.)' },
  एतम्: { lemma: 'एतद्', label: 'm. acc. sg.' },
  एतेन: { lemma: 'एतद्', label: 'm./n. instr. sg.' },
  एतस्य: { lemma: 'एतद्', label: 'm./n. gen. sg.' },
  एतस्मात्: { lemma: 'एतद्', label: 'm./n. abl. sg.' },
  एतस्मिन्: { lemma: 'एतद्', label: 'm./n. loc. sg.' },
  एते: { lemma: 'एतद्', label: 'm. nom. pl.' },
  एतत्: { lemma: 'एतद्', label: 'n. nom./acc. sg.', gloss: 'this (n.)' },
  एतानि: { lemma: 'एतद्', label: 'n. nom./acc. pl.' },
  एषा: { lemma: 'एतद्', label: 'f. nom. sg.' },
  एताम्: { lemma: 'एतद्', label: 'f. acc. sg.' },
  एतेषाम्: { lemma: 'एतद्', label: 'm./n. gen. pl.' },

  // इदम्
  अयम्: { lemma: 'इदम्', label: 'm. nom. sg.', gloss: 'this (m.)' },
  इमम्: { lemma: 'इदम्', label: 'm. acc. sg.' },
  अनेन: { lemma: 'इदम्', label: 'm./n. instr. sg.' },
  अस्य: { lemma: 'इदम्', label: 'm./n. gen. sg.' },
  अस्मात्: { lemma: 'इदम्', label: 'm./n. abl. sg.', gloss: 'from this' },
  अस्मिन्: { lemma: 'इदम्', label: 'm./n. loc. sg.' },
  इमे: { lemma: 'इदम्', label: 'm. nom. pl.' },
  इदम्: { lemma: 'इदम्', label: 'n. nom./acc. sg.', gloss: 'this (n.)' },
  इमानि: { lemma: 'इदम्', label: 'n. nom./acc. pl.' },
  इयम्: { lemma: 'इदम्', label: 'f. nom. sg.' },
  इमाम्: { lemma: 'इदम्', label: 'f. acc. sg.' },
  एषाम्: { lemma: 'इदम्', label: 'm./n. gen. pl.' },
  एभिः: { lemma: 'इदम्', label: 'm./n. instr. pl.' },

  // अदस् ("that, yon")
  असौ: { lemma: 'अदस्', label: 'm./f. nom. sg.', gloss: 'that yonder' },
  अमुम्: { lemma: 'अदस्', label: 'm. acc. sg.' },
  अमुना: { lemma: 'अदस्', label: 'm./n. instr. sg.' },
  अमुष्य: { lemma: 'अदस्', label: 'm./n. gen. sg.' },
  अमी: { lemma: 'अदस्', label: 'm. nom. pl.' },
  अदः: { lemma: 'अदस्', label: 'n. nom./acc. sg.' },

  // किम्
  कः: { lemma: 'किम्', label: 'm. nom. sg.', gloss: 'who?' },
  कम्: { lemma: 'किम्', label: 'm. acc. sg.' },
  केन: { lemma: 'किम्', label: 'm./n. instr. sg.', gloss: 'by whom?' },
  कस्य: { lemma: 'किम्', label: 'm./n. gen. sg.' },
  कस्मात्: { lemma: 'किम्', label: 'm./n. abl. sg.', gloss: 'from whom? why?' },
  कस्मिन्: { lemma: 'किम्', label: 'm./n. loc. sg.' },
  के: { lemma: 'किम्', label: 'm. nom. pl.' },
  किम्: { lemma: 'किम्', label: 'n. nom./acc. sg.', gloss: 'what?' },
  कानि: { lemma: 'किम्', label: 'n. nom./acc. pl.' },
  का: { lemma: 'किम्', label: 'f. nom. sg.' },
  काम्: { lemma: 'किम्', label: 'f. acc. sg.' },

  // अहम् / त्वम् (1st & 2nd person)
  अहम्: { lemma: 'अस्मद्', label: '1 sg.', gloss: 'I' },
  आवाम्: { lemma: 'अस्मद्', label: '1 dual', gloss: 'we two' },
  वयम्: { lemma: 'अस्मद्', label: '1 pl.', gloss: 'we' },
  माम्: { lemma: 'अस्मद्', label: '1 acc. sg.', gloss: 'me' },
  मा: { lemma: 'अस्मद्', label: '1 acc. sg. (encl.)' },
  मया: { lemma: 'अस्मद्', label: '1 instr. sg.' },
  मह्यम्: { lemma: 'अस्मद्', label: '1 dat. sg.' },
  मे: { lemma: 'अस्मद्', label: '1 dat./gen. sg. (encl.)' },
  मम: { lemma: 'अस्मद्', label: '1 gen. sg.', gloss: 'mine' },
  मत्: { lemma: 'अस्मद्', label: '1 abl. sg.' },
  मयि: { lemma: 'अस्मद्', label: '1 loc. sg.' },
  अस्मान्: { lemma: 'अस्मद्', label: '1 acc. pl.' },
  अस्माभिः: { lemma: 'अस्मद्', label: '1 instr. pl.' },
  अस्मभ्यम्: { lemma: 'अस्मद्', label: '1 dat. pl.' },
  अस्माकम्: { lemma: 'अस्मद्', label: '1 gen. pl.' },
  त्वम्: { lemma: 'युष्मद्', label: '2 sg.', gloss: 'thou' },
  युवाम्: { lemma: 'युष्मद्', label: '2 dual', gloss: 'you two' },
  यूयम्: { lemma: 'युष्मद्', label: '2 pl.', gloss: 'you (pl.)' },
  त्वाम्: { lemma: 'युष्मद्', label: '2 acc. sg.' },
  त्वा: { lemma: 'युष्मद्', label: '2 acc. sg. (encl.)' },
  त्वया: { lemma: 'युष्मद्', label: '2 instr. sg.' },
  तुभ्यम्: { lemma: 'युष्मद्', label: '2 dat. sg.' },
  ते_2: { lemma: 'युष्मद्', label: '2 dat./gen. sg. (encl.)' },
  तव: { lemma: 'युष्मद्', label: '2 gen. sg.', gloss: 'thy' },
  त्वत्: { lemma: 'युष्मद्', label: '2 abl. sg.' },
  त्वयि: { lemma: 'युष्मद्', label: '2 loc. sg.' },
  युष्मान्: { lemma: 'युष्मद्', label: '2 acc. pl.' },
  युष्माभिः: { lemma: 'युष्मद्', label: '2 instr. pl.' },
  युष्माकम्: { lemma: 'युष्मद्', label: '2 gen. pl.' },

  // आत्मन् (commonly used as a reflexive)
  आत्मा: { lemma: 'आत्मन्', label: 'm. nom. sg.' },
  आत्मानम्: { lemma: 'आत्मन्', label: 'm. acc. sg.' },
  आत्मना: { lemma: 'आत्मन्', label: 'm. instr. sg.' },
  आत्मने: { lemma: 'आत्मन्', label: 'm. dat. sg.' },
  आत्मनः: { lemma: 'आत्मन्', label: 'm. abl./gen. sg.' },
  आत्मनि: { lemma: 'आत्मन्', label: 'm. loc. sg.' },
  आत्मानौ: { lemma: 'आत्मन्', label: 'm. nom./acc. dual' },
  आत्मानः: { lemma: 'आत्मन्', label: 'm. nom. pl.' },

  // ब्रह्मन्
  ब्रह्मा: { lemma: 'ब्रह्मन्', label: 'm./n. nom. sg.' },
  ब्रह्माणम्: { lemma: 'ब्रह्मन्', label: 'm. acc. sg.' },
  ब्रह्मणा: { lemma: 'ब्रह्मन्', label: 'm./n. instr. sg.' },
  ब्रह्मणे: { lemma: 'ब्रह्मन्', label: 'm./n. dat. sg.' },
  ब्रह्मणः: { lemma: 'ब्रह्मन्', label: 'm./n. abl./gen. sg.' },
  ब्रह्मणि: { lemma: 'ब्रह्मन्', label: 'm./n. loc. sg.' },
  ब्रह्मणाम्: { lemma: 'ब्रह्मन्', label: 'm./n. gen. pl.' },

  // Common irregular verb forms
  उवाच: { lemma: 'वच्', label: 'pf. 3 sg.', gloss: '(he) said' },
  उवाचुः: { lemma: 'वच्', label: 'pf. 3 pl.' },
  ऊचुः: { lemma: 'वच्', label: 'pf. 3 pl.', gloss: '(they) said' },
  आह: { lemma: 'अह्', label: 'pf. 3 sg.', gloss: '(he/she) said' },
  आहुः: { lemma: 'अह्', label: 'pf. 3 pl.' },
  अस्ति: { lemma: 'अस्', label: 'pres. 3 sg.', gloss: 'is' },
  सन्ति: { lemma: 'अस्', label: 'pres. 3 pl.', gloss: 'are' },
  आसीत्: { lemma: 'अस्', label: 'impf. 3 sg.', gloss: 'was' },
  आसन्: { lemma: 'अस्', label: 'impf. 3 pl.' },
  स्यात्: { lemma: 'अस्', label: 'opt. 3 sg.', gloss: 'may be' },
  भवति: { lemma: 'भू', label: 'pres. 3 sg.', gloss: 'becomes, is' },
  भवन्ति: { lemma: 'भू', label: 'pres. 3 pl.' },
  बभूव: { lemma: 'भू', label: 'pf. 3 sg.' },
  करोति: { lemma: 'कृ', label: 'pres. 3 sg.', gloss: 'does' },
  कुर्वन्ति: { lemma: 'कृ', label: 'pres. 3 pl.' },
  कुर्यात्: { lemma: 'कृ', label: 'opt. 3 sg.' },
  चकार: { lemma: 'कृ', label: 'pf. 3 sg.' },
  कृतम्: { lemma: 'कृ', label: 'pp. n. nom. sg.', gloss: 'done' },
  कृतः: { lemma: 'कृ', label: 'pp. m. nom. sg.' },
  कर्ता: { lemma: 'कर्तृ', label: 'm. nom. sg.', gloss: 'doer' },
  गच्छति: { lemma: 'गम्', label: 'pres. 3 sg.', gloss: 'goes' },
  गतः: { lemma: 'गम्', label: 'pp. m. nom. sg.', gloss: 'gone' },
  गत्वा: { lemma: 'गम्', label: 'absolutive' },
  जगाम: { lemma: 'गम्', label: 'pf. 3 sg.' },
  वेद: { lemma: 'विद्', label: 'pf. 3 sg.', gloss: 'knows' },
  विद्यते: { lemma: 'विद्', label: 'pres. 3 sg. (pas.)', gloss: 'is found, exists' },
  विद्यन्ते: { lemma: 'विद्', label: 'pres. 3 pl. (pas.)' },
  उच्यते: { lemma: 'वच्', label: 'pres. 3 sg. (pas.)', gloss: 'is said' },
  उच्यन्ते: { lemma: 'वच्', label: 'pres. 3 pl. (pas.)' },
  जानाति: { lemma: 'ज्ञा', label: 'pres. 3 sg.', gloss: 'knows' },
  जानन्ति: { lemma: 'ज्ञा', label: 'pres. 3 pl.' },
  ज्ञात्वा: { lemma: 'ज्ञा', label: 'absolutive' },
  ज्ञातम्: { lemma: 'ज्ञा', label: 'pp. n. nom. sg.', gloss: 'known' },
  जातः: { lemma: 'जन्', label: 'pp. m. nom. sg.', gloss: 'born' },
  पश्यति: { lemma: 'दृश्', label: 'pres. 3 sg.', gloss: 'sees' },
  पश्यन्ति: { lemma: 'दृश्', label: 'pres. 3 pl.' },
  ददर्श: { lemma: 'दृश्', label: 'pf. 3 sg.' },
  दृष्टम्: { lemma: 'दृश्', label: 'pp. n. nom. sg.', gloss: 'seen' },
  ददाति: { lemma: 'दा', label: 'pres. 3 sg.', gloss: 'gives' },
  दत्तम्: { lemma: 'दा', label: 'pp. n. nom. sg.', gloss: 'given' },

  // Common indeclinables / particles
  एवम्: { lemma: 'एवम्', label: 'adv.', gloss: 'thus' },
  कथम्: { lemma: 'कथम्', label: 'adv.', gloss: 'how?' },
  यथा: { lemma: 'यथा', label: 'adv.', gloss: 'as, in the manner of' },
  तथा: { lemma: 'तथा', label: 'adv.', gloss: 'so, in that way' },
  यदा: { lemma: 'यदा', label: 'adv.', gloss: 'when' },
  तदा: { lemma: 'तदा', label: 'adv.', gloss: 'then' },
  कदा: { lemma: 'कदा', label: 'adv.', gloss: 'when?' },
  सदा: { lemma: 'सदा', label: 'adv.', gloss: 'always' },
  सर्वदा: { lemma: 'सर्वदा', label: 'adv.', gloss: 'always' },
  यत्र: { lemma: 'यत्र', label: 'adv.', gloss: 'where' },
  तत्र: { lemma: 'तत्र', label: 'adv.', gloss: 'there' },
  कुत्र: { lemma: 'कुत्र', label: 'adv.', gloss: 'where?' },
  अत्र: { lemma: 'अत्र', label: 'adv.', gloss: 'here' },
  अद्य: { lemma: 'अद्य', label: 'adv.', gloss: 'today' },
  अधुना: { lemma: 'अधुना', label: 'adv.', gloss: 'now' },
  अस्तु: { lemma: 'अस्', label: 'imp. 3 sg.', gloss: 'let it be' },
  सन्तु: { lemma: 'अस्', label: 'imp. 3 pl.' },
  चेत्: { lemma: 'चेत्', label: 'pcl.', gloss: 'if' },
  यदि: { lemma: 'यदि', label: 'pcl.', gloss: 'if' },
  ननु: { lemma: 'ननु', label: 'pcl.', gloss: 'surely; (objection)' },
  नु: { lemma: 'नु', label: 'pcl.', gloss: 'now, indeed' },
  ह्: { lemma: 'ह', label: 'pcl.', gloss: 'indeed (Vedic)' },
  वै: { lemma: 'वै', label: 'pcl.', gloss: 'indeed (Vedic)' },
  खलु: { lemma: 'खलु', label: 'pcl.', gloss: 'indeed' },
  सु: { lemma: 'सु', label: 'prefix', gloss: 'well-, good-' },
  कु: { lemma: 'कु', label: 'prefix', gloss: 'bad-' },
  निर्: { lemma: 'निर्', label: 'prefix', gloss: 'without, out of' },
  दुर्: { lemma: 'दुर्', label: 'prefix', gloss: 'difficult-' },
  ना: { lemma: 'न', label: 'pcl.', gloss: 'not' },
  नैव: { lemma: 'न', label: 'pcl. + एव', gloss: 'not at all' },
  नेति: { lemma: 'न', label: 'pcl. + इति', gloss: '"not this", "not so"' },
  चेति: { lemma: 'च', label: 'pcl. + इति', gloss: '"and thus"' },
  तन्न: { lemma: 'तद्', label: '+ न', gloss: 'that not' },
  तच्च: { lemma: 'तद्', label: '+ च', gloss: 'and that' },
  सः2: { lemma: 'तद्', label: '' },
  सर्वम्: { lemma: 'सर्व', label: 'n. nom./acc. sg.', gloss: 'all, everything' },
  सर्वः: { lemma: 'सर्व', label: 'm. nom. sg.' },
  सर्वा: { lemma: 'सर्व', label: 'f. nom. sg.' },
  सर्वे: { lemma: 'सर्व', label: 'm. nom. pl.' },
  सर्वाणि: { lemma: 'सर्व', label: 'n. nom./acc. pl.' },
  सर्वेषाम्: { lemma: 'सर्व', label: 'm./n. gen. pl.' },
  सर्वस्य: { lemma: 'सर्व', label: 'm./n. gen. sg.' },
  आदि: { lemma: 'आदि', label: 'm.', gloss: 'beginning' },
  आदिः: { lemma: 'आदि', label: 'm. nom. sg.' },
  आदौ: { lemma: 'आदि', label: 'm. loc. sg.', gloss: 'at the beginning' },
  सिद्धम्: { lemma: 'सिध्', label: 'pp.', gloss: 'established' },
  सिद्धान्तः: { lemma: 'सिद्धान्त', label: 'm. nom. sg.', gloss: 'settled conclusion' },
  व्याख्या: { lemma: 'व्याख्या', label: 'f.', gloss: 'explanation, gloss' },
  व्याख्यानम्: { lemma: 'व्याख्यान', label: 'n. nom./acc. sg.', gloss: 'explanation' },
  // श्रुति
  श्रुतेः: { lemma: 'श्रुति', label: 'f. abl./gen. sg.', gloss: 'from the Veda' },
  श्रुतौ: { lemma: 'श्रुति', label: 'f. loc. sg.', gloss: 'in the Veda' },
  // प्राण
  प्राणो: { lemma: 'प्राण', label: 'm. nom. sg.', gloss: 'vital breath' },
  प्राणं: { lemma: 'प्राण', label: 'm. acc. sg.' },
  प्राणाः: { lemma: 'प्राण', label: 'm. nom. pl.' },
  प्राणाद्: { lemma: 'प्राण', label: 'm. abl. sg.' },
  // Common visarga-sandhi nominatives
  यो: { lemma: 'यद्', label: 'm. nom. sg. (sandhi)', gloss: 'who' },
  सो: { lemma: 'तद्', label: 'm. nom. sg. (sandhi)', gloss: 'he' },
  अतो: { lemma: 'अतस्', label: 'adv. (sandhi)', gloss: 'therefore' },
  ततो: { lemma: 'ततस्', label: 'adv. (sandhi)', gloss: 'thence' },
  यतो: { lemma: 'यतस्', label: 'adv. (sandhi)', gloss: 'whence' },
  कुतो: { lemma: 'कुतस्', label: 'adv. (sandhi)', gloss: 'whence?' },
};
